import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import { type OwnerRole } from '../context/RolesContext'

export type CommandCategory = 'route' | 'action' | 'recent'

export type PaletteCommand = {
  id: string
  title: string
  category: CommandCategory
  hint?: string
  keywords?: string[]
  perform: () => void
}

export const getStandardOwnerCommands = (onNavigate?: (route: OwnerRoute) => void, role: OwnerRole = 'founder'): PaletteCommand[] => {
  const nav = onNavigate ?? navigateOwner

  const commands: PaletteCommand[] = [
    {
      id: 'cmd-goto-home',
      title: 'Go to Home (Today KPIs)',
      category: 'route',
      hint: 'g h',
      keywords: ['dashboard', 'metrics', 'today', 'overview'],
      perform: () => nav({ view: 'home' }),
    },
    {
      id: 'cmd-goto-workspaces',
      title: 'Go to Workspaces',
      category: 'route',
      hint: 'g w',
      keywords: ['tenants', 'accounts', 'directory'],
      perform: () => nav({ view: 'workspaces' }),
    },
    {
      id: 'cmd-goto-inbox',
      title: 'Go to Support Inbox',
      category: 'route',
      hint: 'g i',
      keywords: ['tickets', 'support', 'debug', 'help'],
      perform: () => nav({ view: 'inbox' }),
    },
    {
      id: 'cmd-goto-feedback',
      title: 'Go to Feedback Responses',
      category: 'route',
      keywords: ['ratings', 'surveys', 'nps'],
      perform: () => nav({ view: 'feedback' }),
    },
    {
      id: 'cmd-goto-ops',
      title: 'Go to Ops & Incidents',
      category: 'route',
      keywords: ['health', 'status', 'jobs', 'errors'],
      perform: () => nav({ view: 'ops' }),
    },
    {
      id: 'cmd-goto-logs',
      title: 'Go to Logs & Telemetry',
      category: 'route',
      keywords: ['logs', 'telemetry', 'trace', 'errors', 'client', 'events'],
      perform: () => nav({ view: 'logs' }),
    },
    {
      id: 'cmd-goto-analytics',
      title: 'Go to Product Analytics',
      category: 'route',
      keywords: ['retention', 'adoption', 'usage'],
      perform: () => nav({ view: 'analytics' }),
    },
    {
      id: 'cmd-goto-revenue',
      title: 'Go to Revenue',
      category: 'route',
      keywords: ['mrr', 'billing', 'arr', 'subscriptions'],
      perform: () => nav({ view: 'revenue' }),
    },
    {
      id: 'cmd-goto-costs',
      title: 'Go to Costs',
      category: 'route',
      keywords: ['infrastructure', 'ai', 'firebase', 'gcp'],
      perform: () => nav({ view: 'costs' }),
    },
    {
      id: 'cmd-goto-settings',
      title: 'Go to Settings',
      category: 'route',
      keywords: ['config', 'preferences', 'defaults'],
      perform: () => nav({ view: 'config-settings' }),
    },
    {
      id: 'cmd-goto-templates',
      title: 'Go to Explore Templates',
      category: 'route',
      keywords: ['factory', 'gallery', 'seeds'],
      perform: () => nav({ view: 'config-templates' }),
    },
    {
      id: 'cmd-goto-integrations',
      title: 'Go to Integrations Audit',
      category: 'route',
      keywords: ['oauth', 'google', 'slack', 'notion', 'jira'],
      perform: () => nav({ view: 'config-integrations' }),
    },
    {
      id: 'cmd-goto-demo-lane',
      title: 'Go to Demo lane',
      category: 'route',
      keywords: ['demo', 'website', 'roll back', 'release'],
      perform: () => nav({ view: 'config-demo-lane' }),
    },
    {
      id: 'cmd-goto-line',
      title: 'Go to Line AI Policy',
      category: 'route',
      keywords: ['safety', 'ai', 'rules', 'heuristics'],
      perform: () => nav({ view: 'config-line' }),
    },
    {
      id: 'cmd-goto-actions',
      title: 'Go to Actions & Runbooks',
      category: 'route',
      keywords: ['commands', 'grants', 'credits', 'maintenance'],
      perform: () => nav({ view: 'config-actions' }),
    },
    ...(role === 'founder' ? [{
      id: 'cmd-goto-activity',
      title: 'Go to Activity',
      category: 'route' as const,
      keywords: ['audit', 'owner actions', 'history'],
      perform: () => nav({ view: 'activity' }),
    }] : []),
    {
      id: 'cmd-goto-playground',
      title: 'Go to Design Kit Playground',
      category: 'route',
      keywords: ['tokens', 'components', 'table', 'cards', 'ui'],
      perform: () => nav({ view: 'config-playground' }),
    },
    {
      id: 'cmd-action-grant-credits',
      title: 'Action: Grant Workspace Credits',
      category: 'action',
      keywords: ['wallet', 'topup', 'give'],
      perform: () => nav({ view: 'config-actions' }),
    },
    {
      id: 'cmd-action-force-refresh',
      title: 'Action: Force Refresh Auth Token',
      category: 'action',
      keywords: ['claims', 'systemAdmin', 'sync'],
      perform: () => window.location.reload(),
    },
  ]
  return commands
}
